'use client';
import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

const faqs = [
  {
    id: 'faq-who',
    q: 'Who can participate in OnlineOlympiads?',
    a: 'Any school student aged 7–18 can participate. Each individual age (7, 8, 9, 10 … 18) has its own dedicated test, ensuring questions are perfectly calibrated to that age\'s level. No school affiliation is required — students can register independently.',
  },
  {
    id: 'faq-recognized',
    q: 'Is this internationally recognized?',
    a: 'OnlineOlympiads.org olympiads are designed to international academic standards and are participated in by students from 40+ countries. Certificates include a unique verification ID and are recognized by participating schools and educational institutions globally.',
  },
  {
    id: 'faq-rank',
    q: 'How are rankings calculated?',
    a: 'Rankings are calculated based on total score, then by time taken (faster completions rank higher on equal scores). You receive a global rank, country rank, state rank, city rank, and school rank — giving you a complete picture of where you stand at every level.',
  },
  {
    id: 'faq-online',
    q: 'Is the exam fully online?',
    a: 'Yes. All exams are 100% online. Students can take the exam from home, school, or any location with a stable internet connection. The exam is browser-based and works on desktops, laptops, and tablets.',
  },
  {
    id: 'faq-home',
    q: 'Can students take the exam from home?',
    a: 'Absolutely. There is no fixed exam window — students can take the exam anytime at their convenience. Basic integrity measures are in place to ensure fairness.',
  },
  {
    id: 'faq-cert',
    q: 'How are certificates generated?',
    a: 'Certificates are generated on the spot upon result publication. Each certificate has a unique digital ID that can be verified at verify.onlineolympiads.org. Certificates are available as high-resolution PDF downloads and are also sent directly to your registered email ID.',
  },
  {
    id: 'faq-cheat',
    q: 'How is cheating prevented?',
    a: 'We use a combination of randomized question pools, fullscreen enforcement, tab-switch detection, timer enforcement, and anomaly detection. Each student gets a unique question set. Students are allowed a maximum of 2 attempts per olympiad per year.',
  },
  {
    id: 'faq-school',
    q: 'Are schools required to participate?',
    a: 'No. Individual students can register and participate independently. Schools can optionally register as institutional partners to access bulk pricing and school-level recognition.',
  },
];

export default function HomeFAQ() {
  const [open, setOpen] = useState<string | null>('faq-who');

  return (
    <section id="faq" className="py-24 bg-background">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-14">
          <span className="inline-block px-3 py-1 text-xs font-semibold text-secondary bg-secondary/10 rounded-full mb-3 tracking-wide uppercase">
            FAQ
          </span>
          <h2 className="text-3xl xl:text-4xl font-extrabold text-foreground mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Everything you need to know before registering.
          </p>
        </div>

        <div className="max-w-3xl mx-auto space-y-3">
          {faqs?.map((faq) => (
            <div
              key={faq?.id}
              className="bg-card border border-border rounded-xl overflow-hidden shadow-card"
            >
              <button
                className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-muted/50 transition-colors"
                onClick={() => setOpen(open === faq?.id ? null : faq?.id)}
                aria-expanded={open === faq?.id}
              >
                <span className="text-sm font-semibold text-foreground pr-4">{faq?.q}</span>
                <ChevronDown
                  size={16}
                  className={`flex-shrink-0 text-muted-foreground transition-transform duration-200 ${open === faq?.id ? 'rotate-180' : ''}`}
                />
              </button>
              {open === faq?.id && (
                <div className="px-6 pb-5 fade-in">
                  <p className="text-sm text-muted-foreground leading-relaxed">{faq?.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}